import { useEffect, useRef, type ChangeEvent, type KeyboardEvent } from "react";
import { Search, X } from "lucide-react";
import { fieldCls } from "../AuthShell.js";

export function SearchInput({
  value,
  onChange,
  onDebouncedChange,
  debounceMs = 300,
  placeholder,
  id,
  "aria-label": ariaLabel,
  className,
  maxLength,
}: {
  value: string;
  onChange: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
  debounceMs?: number;
  placeholder?: string;
  id?: string;
  "aria-label"?: string;
  className?: string;
  maxLength?: number;
}) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function flushDebounce(next: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    onDebouncedChangeRef.current?.(next);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    onChange(next);
    if (!onDebouncedChangeRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onDebouncedChangeRef.current?.(next.trim());
    }, debounceMs);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      // Keep this on the focused input so Escape clears a filter without
      // unexpectedly affecting dialogs or other page-level shortcuts.
      if (value) {
        e.preventDefault();
        e.stopPropagation();
        clear();
      }
      return;
    }
    if (e.key === "Enter" && onDebouncedChangeRef.current) {
      e.preventDefault();
      flushDebounce(value.trim());
    }
  }

  function clear() {
    onChange("");
    flushDebounce("");
  }

  return (
    <div className={["relative max-w-sm", className ?? ""].filter(Boolean).join(" ")}>
      <span
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        aria-hidden="true"
      >
        <Search size={16} />
      </span>
      <input
        id={id}
        type="text"
        className={`${fieldCls} !mt-0 pl-9 ${value ? "pr-9" : ""}`}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        maxLength={maxLength}
      />
      {value ? (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          aria-label="Clear search"
          onClick={clear}
        >
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
